/**
 * Tableau des flux de trésorerie — modèle OFFICIEL OHADA (Système Normal).
 *
 * ⚠️ Cet état manquait entièrement au portage, avec trois autres sous-onglets.
 * Ce n'est pas un tableau de plus : c'est le troisième état de synthèse exigé
 * par l'OHADA, à côté du bilan et du compte de résultat. Sans lui, le dossier
 * remis à l'expert-comptable est incomplet.
 *
 * Les codes (ZA, FA, ZB…) sont ceux de la liasse : ils ne sont pas décoratifs,
 * ils permettent de rapprocher ligne à ligne avec le formulaire officiel.
 */
export interface LigneFlux {
  /** Titre de section — pas de montant. */
  section?: string
  ref?: string
  libelle?: string
  /** Renvoi de la liasse (« B », « G = B+C+F »). */
  note?: string
  valeur?: number
  /** Sous-total : filet et gras. */
  total?: boolean
  /** Grand total : fond crème et majuscules. */
  grand?: boolean
}

/**
 * Construit les lignes à partir des valeurs du compte de résultat et du bilan.
 *
 * `resultat` et `bilan` sont les tables REF→montant déjà calculées pour les
 * deux autres états : le flux en dérive, il ne recalcule rien. C'est ce qui
 * garantit que les trois états ne peuvent pas se contredire.
 */
export function tableauFlux(
  resultat: Record<string, number>,
  bilan: Record<string, number>,
): { lignes: LigneFlux[]; cloture: number; controle: number } {
  const r = (k: string) => resultat[k] ?? 0
  const b = (k: string) => bilan[k] ?? 0

  /* Premier exercice suivi : la trésorerie d'ouverture est nulle, donc la
     variation de la période ÉGALE le solde de clôture. Poser autre chose
     supposerait un exercice antérieur qui n'existe pas encore. */
  const ZA = 0

  // Capacité d'autofinancement globale, puis variations du cycle d'exploitation.
  const FA = r('XI') + r('RL') + r('RN') - r('TJ') - r('TL') + r('RO') - r('TN')
  const FB = -b('BA')
  const FC = -b('BB')
  const FD = -b('BG')
  const FE = b('DP')
  const ZB = FA + FB + FC + FD + FE

  const FF = -b('AD')
  const FG = -b('AI')
  const FH = -b('AQ')
  const FI = 0
  const FJ = 0
  const ZC = FF + FG + FH + FI + FJ

  const FK = b('CA') - b('CB') + b('CD') + b('CE') + b('CF') + b('CG') + b('CH')
  const FL = b('CL')
  const FM = 0
  const FN = 0
  const ZD = FK + FL - FM - FN

  const FO = b('DA')
  const FP = b('DB')
  const FQ = 0
  const ZE = FO + FP - FQ

  const ZF = ZD + ZE
  const ZG = ZB + ZC + ZF
  const ZH = ZG + ZA

  /* Le CONTRÔLE : la trésorerie de clôture reconstituée par les flux doit
     égaler la trésorerie nette du bilan (actif moins passif). Un écart
     signale une écriture qui ne passe pas par le bon compte — c'est le seul
     garde-fou automatique de tout l'état. */
  const controle = b('BT') - b('DT')

  return {
    cloture: ZH,
    controle,
    lignes: [
      { ref: 'ZA', libelle: 'Trésorerie nette au 1er janvier', note: 'A', valeur: ZA, total: true },
      { section: 'Flux de trésorerie provenant des activités opérationnelles' },
      { ref: 'FA', libelle: "Capacité d'autofinancement globale (CAFG)", valeur: FA },
      { ref: 'FB', libelle: "(-) Variation de l'actif circulant HAO", valeur: FB },
      { ref: 'FC', libelle: '(-) Variation des stocks', valeur: FC },
      { ref: 'FD', libelle: '(-) Variation des créances', valeur: FD },
      { ref: 'FE', libelle: '(+) Variation du passif circulant', valeur: FE },
      { ref: 'ZB', libelle: 'Flux de trésorerie des activités opérationnelles', note: 'B', valeur: ZB, total: true },
      { section: "Flux de trésorerie provenant des activités d'investissement" },
      { ref: 'FF', libelle: "(-) Décaissements / acquisitions d'immobilisations incorporelles", valeur: FF },
      { ref: 'FG', libelle: "(-) Décaissements / acquisitions d'immobilisations corporelles", valeur: FG },
      { ref: 'FH', libelle: "(-) Décaissements / acquisitions d'immobilisations financières", valeur: FH },
      { ref: 'FI', libelle: "(+) Encaissements / cessions d'immobilisations incorp. & corp.", valeur: FI },
      { ref: 'FJ', libelle: "(+) Encaissements / cessions d'immobilisations financières", valeur: FJ },
      { ref: 'ZC', libelle: "Flux de trésorerie des activités d'investissement", note: 'C', valeur: ZC, total: true },
      { section: 'Flux de trésorerie provenant du financement par capitaux propres' },
      { ref: 'FK', libelle: '(+) Augmentations de capital par apports nouveaux', valeur: FK },
      { ref: 'FL', libelle: "(+) Subventions d'investissement reçues", valeur: FL },
      { ref: 'FM', libelle: '(-) Prélèvements sur le capital', valeur: FM },
      { ref: 'FN', libelle: '(-) Dividendes versés', valeur: FN },
      { ref: 'ZD', libelle: 'Flux de trésorerie / capitaux propres', note: 'D', valeur: ZD, total: true },
      { section: 'Trésorerie provenant du financement par capitaux étrangers' },
      { ref: 'FO', libelle: '(+) Emprunts', valeur: FO },
      { ref: 'FP', libelle: '(+) Autres dettes financières', valeur: FP },
      { ref: 'FQ', libelle: '(-) Remboursements des emprunts et autres dettes financières', valeur: FQ },
      { ref: 'ZE', libelle: 'Flux de trésorerie / capitaux étrangers', note: 'E', valeur: ZE, total: true },
      { ref: 'ZF', libelle: 'Flux de trésorerie des activités de financement', note: 'F = D+E', valeur: ZF, total: true },
      { ref: 'ZG', libelle: 'VARIATION DE LA TRÉSORERIE NETTE DE LA PÉRIODE', note: 'G = B+C+F', valeur: ZG, grand: true },
      { ref: 'ZH', libelle: 'Trésorerie nette au 31 décembre', note: 'H = G+A', valeur: ZH, grand: true },
    ],
  }
}
